import React, { useEffect, useState, useRef } from 'react'
import { Spinner, Alert, Button, Form, InputGroup, Overlay, Popover } from 'react-bootstrap'
import moment from 'moment'
import { useSelector } from 'react-redux'
import ReactTable from 'react-table'
import 'react-table/react-table.css'
import NavHeader from './common/navHeader'
import { requestFmcsaBulkUploadsForUser } from '../../services/apiService'
import { faSync, faArrowLeft, faArrowRight } from '@fortawesome/free-solid-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import DatePicker from 'react-datepicker'
import 'react-datepicker/dist/react-datepicker.css'


const DEFAULT_PAGE_SIZE = 100

const FmcsaBulkUploadList = () => {
  const [uploads, setUploads] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [page, setPage] = useState(1)
  const [pageSize] = useState(DEFAULT_PAGE_SIZE)
  const [pages, setPages] = useState(1)
  const [total, setTotal] = useState(0)
  const [startDate, setStartDate] = useState(moment().subtract(7, 'days').toDate())
  const [endDate, setEndDate] = useState(null)
  const [showDateFilter, setShowDateFilter] = useState(false)
  const filterBtnRef = useRef(null)
  const token = useSelector(state => state.auth.token)

  useEffect(() => {
    fetchUploads()
    // eslint-disable-next-line
  }, [token, page, pageSize, startDate, endDate])

  const fetchUploads = async () => {
    setLoading(true)
    setError(null)
    try {
      const params = {
        page,
        pageSize,
        startDate: startDate ? moment(startDate).format('YYYY-MM-DD') : undefined,
        endDate: endDate ? moment(endDate).format('YYYY-MM-DD') : undefined
      }
      const res = await requestFmcsaBulkUploadsForUser(token, params)
      setUploads(res.uploads || [])
      setPages(res.pages?.pages || 1)
      setTotal(res.pages?.total || 0)
    } catch (err) {
      setError('Could not load FMCSA uploads.')
    }
    setLoading(false)
  }

  const handlePrev = () => setPage(p => Math.max(1, p - 1))
  const handleNext = () => setPage(p => Math.min(pages, p + 1))
  const handlePageInput = e => {
    let val = Number(e.target.value)
    if (!isNaN(val) && val >= 1 && val <= pages) setPage(val)
  }
  const handleStartDate = date => { setStartDate(date); setPage(1) }
  const handleEndDate = date => { setEndDate(date); setPage(1) }
  const handleToggleDateFilter = () => setShowDateFilter(v => !v)

  // Format the current date range for the title
  const displayStart = moment(startDate).format('MMM D, YYYY')
  const displayEnd = endDate ? moment(endDate).format('MMM D, YYYY') : moment().format('MMM D, YYYY')
  const dateRangeTitle = `${displayStart} - ${displayEnd}`

  const columns = [
    {
      Header: 'File Name',
      accessor: 'fileName',
      headerClassName: 'centered',
      className: 'centered',
      minWidth: 150
    },
    {
      Header: 'Uploaded',
      accessor: 'createdAt',
      Cell: row => moment(row.value).format('YYYY-MM-DD HH:mm'),
      headerClassName: 'centered',
      className: 'centered',
      minWidth: 150
    },
    {
      Header: 'Total Drivers',
      accessor: 'totalRows',
      headerClassName: 'centered',
      className: 'centered',
      minWidth: 80
    },
    {
      Header: 'Processed',
      accessor: 'rowsProcessed',
      headerClassName: 'centered',
      className: 'centered',
      minWidth: 80
    },
    {
      Header: 'Failed',
      accessor: 'rowsFailed',
      headerClassName: 'centered',
      className: 'centered',
      minWidth: 80
    },
    {
      Header: 'Status',
      accessor: 'status',
      Cell: row => {
        const status = row.value
        const upload = row.original
        if (status === 'Completed' && upload.completed) {
          return (
            <span>
              Completed<br />
              <span className='text-muted' style={{ fontSize: '0.9em' }}>
                {moment(upload.completed).format('YYYY-MM-DD HH:mm')}
              </span>
            </span>
          )
        }
        return status
      },
      headerClassName: 'centered',
      className: 'centered',
      minWidth: 120
    },
    {
      Header: 'Results',
      id: 'results',
      accessor: d => d,
      Cell: row => {
        const upload = row.value
        if (upload.resultsZipUrl) {
          return (
            <Button
              size='sm'
              variant='success'
              href={upload.resultsZipUrl}
              target='_blank'
              rel='noopener noreferrer'
            >
              Download
            </Button>
          )
        } else if (
          upload.status === 'Failed' &&
          Array.isArray(upload.errorInfo) &&
          upload.errorInfo.length > 0
        ) {
          return (
            <Alert variant='danger' className='mb-0 p-1'>
              {upload.errorInfo.map((err, idx) => (
                <div key={idx}>{err.errorMessage}</div>
              ))}
            </Alert>
          )
        } else {
          return <span>-</span>
        }
      },
      headerClassName: 'centered',
      className: 'centered',
      minWidth: 120
    }
  ]

  return (
    <div className='d-flex flex-column w-100 h-100'>
      <NavHeader />
      <div className='flex-grow p-3 mt-3'>
        <div className='d-flex justify-content-between align-items-center mb-2'>
          <h5 className='mb-0'>FMCSA Bulk Uploads <span className='text-muted' style={{ fontSize: '0.95em' }}>{dateRangeTitle}</span></h5>
          <Form.Group className='d-flex flex-column align-items-end mb-0'>
            <Form.Label>Actions</Form.Label>
            <InputGroup className='d-flex justify-content-end'>
              <Button
                variant='outline-primary'
                size='sm'
                className='mr-2'
                disabled={loading}
                title='Reload data'
                onClick={fetchUploads}
              ><FontAwesomeIcon icon={faSync} /></Button>
              <Button
                ref={filterBtnRef}
                variant='outline-secondary'
                size='sm'
                className='mr-2'
                onClick={handleToggleDateFilter}
              >Filter by Date</Button>
            </InputGroup>
          </Form.Group>
        </div>
        <Overlay
          show={showDateFilter}
          target={filterBtnRef.current}
          placement='bottom'
          containerPadding={20}
          rootClose
          onHide={() => setShowDateFilter(false)}
        >
          <Popover id='date-filter-popover'>
            <Popover.Title as='h3'>Filter by Date</Popover.Title>
            <Popover.Content>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem', alignItems: 'center', minWidth: 250 }}>
                <div>
                  <div className='mb-1'>Start Date</div>
                  <DatePicker
                    selected={startDate}
                    onChange={handleStartDate}
                    dateFormat='yyyy-MM-dd'
                    maxDate={endDate || undefined}
                    isClearable
                    placeholderText='Start Date'
                    inline
                  />
                </div>
                <div>
                  <div className='mb-1'>End Date</div>
                  <DatePicker
                    selected={endDate}
                    onChange={handleEndDate}
                    dateFormat='yyyy-MM-dd'
                    minDate={startDate || undefined}
                    isClearable
                    placeholderText='End Date'
                    inline
                  />
                </div>
              </div>
            </Popover.Content>
          </Popover>
        </Overlay>
        {loading ? (
          <div className='d-flex justify-content-center align-items-center' style={{ minHeight: 200 }}>
            <Spinner animation='border' size='sm' />
          </div>
        ) : error ? (
          <Alert variant='danger'>{error}</Alert>
        ) : !uploads.length ? (
          <Alert variant='info'>No uploads found.</Alert>
        ) : (
          <>
            <ReactTable
              data={uploads}
              columns={columns}
              minRows={1}
              showPagination={false}
              className='-striped -highlight'
              getTrProps={() => ({ className: '' })}
            />
            <div className='d-flex justify-content-between align-items-center mt-2'>
              <div>
                Showing {uploads.length} of {total} uploads
              </div>
              <div className='d-flex align-items-center'>
                <Button
                  variant='outline-secondary'
                  size='sm'
                  className='mr-2'
                  disabled={page <= 1}
                  onClick={handlePrev}
                  title='Prev'
                >
                  <FontAwesomeIcon icon={faArrowLeft} />
                </Button>
                <span className='mr-2'>Page</span>
                <Form.Control
                  size='sm'
                  type='number'
                  className='mr-2'
                  value={page}
                  min={1}
                  max={pages}
                  onChange={handlePageInput}
                  style={{ width: 70 }}
                />
                <span className='mr-2'>out of {pages}</span>
                <Button
                  variant='outline-secondary'
                  size='sm'
                  disabled={page >= pages}
                  onClick={handleNext}
                  title='Next'
                >
                  <FontAwesomeIcon icon={faArrowRight} />
                </Button>
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  )
}

export default FmcsaBulkUploadList